import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthorSubmissions, getAuthorDuplicatePapers } from '../../utils/api';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import Select from '../../components/Select';

import { formatDateOnlyIST } from '../../utils/dateFormatter';
export default function MySubmissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [filteredMyPapers, setFilteredMyPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const [duplicatePapers, setDuplicatePapers] = useState([]);

  // Separate submissions into primary author vs co-author
  const { mySubmissions, myPapers } = useMemo(() => {
    const primary = [];
    const coAuthored = [];

    submissions.forEach((submission) => {
      // Backend now provides isMainAuthor and isCoAuthor flags
      if (submission.isMainAuthor || !submission.isCoAuthor) {
        primary.push(submission);
      } else if (submission.isCoAuthor) {
        coAuthored.push(submission);
      }
    });

    return { mySubmissions: primary, myPapers: coAuthored };
  }, [submissions]);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [submData, dupData] = await Promise.all([
        getAuthorSubmissions(),
        getAuthorDuplicatePapers().catch(() => ({ data: [] }))
      ]);
      setSubmissions(submData.data || submData);
      setDuplicatePapers(dupData.data || []);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError(err.response?.data?.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    if (statusFilter) {
      if (statusFilter === 'duplicate_rejected') {
        setFilteredSubmissions([]);
        setFilteredMyPapers([]);
      } else {
        setFilteredSubmissions(mySubmissions.filter((s) => s.status === statusFilter));
        setFilteredMyPapers(myPapers.filter((s) => s.status === statusFilter));
      }
    } else {
      setFilteredSubmissions(mySubmissions);
      setFilteredMyPapers(myPapers);
    }
  }, [statusFilter, mySubmissions, myPapers]);

  const getStatusBadge = (status) => {
    const variants = {
      under_review: 'warning',
      accepted: 'success',
      rejected: 'danger',
      pending: 'info',
      submitted: 'info',
      submitted_pending_dup_check: 'info',
      submitted_dup_ok: 'info',
      submitted_dup_suspect: 'info',
      revision: 'warning'
    };
    const statusLabels = {
      under_review: 'Under Review',
      accepted: 'Accepted',
      rejected: 'Rejected',
      pending: 'Pending',
      submitted: 'Under Review',
      submitted_pending_dup_check: 'Under Review',
      submitted_dup_ok: 'Under Review',
      submitted_dup_suspect: 'Under Review',
      revision: '⚠️ Revision Needed'
    };
    return <Badge variant={variants[status] || 'default'}>{statusLabels[status] || status?.replace('_', ' ')}</Badge>;
  };

  if (loading) return <Loading />;

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-blue-600 hover:text-blue-800 mb-4"
            >
              ← Back
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Submissions</h1>
          </div>
          <Button
            onClick={() => navigate('/author/discover')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Submit New Paper
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchSubmissions}
              className="text-red-600 hover:text-red-800 font-medium mt-2"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Filter */}
        <div className="mb-6 flex gap-4 items-center">
          <label className="text-sm font-medium text-gray-900">Filter by Status:</label>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="max-w-xs"
          >
            <option value="">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="revision">Revision Needed</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="duplicate_rejected">Duplicate Rejected</option>
          </Select>
        </div>

        {/* My Submissions Section (Primary Author) */}
        {statusFilter !== 'duplicate_rejected' && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">My Submissions</h2>
            <p className="text-sm text-gray-600 mb-4">Papers you submitted as primary author</p>
            
            {filteredSubmissions.length === 0 ? (
              <Card className="text-center py-12">
                <p className="text-gray-600 mb-4">No submissions found</p>
                <Button
                  onClick={() => navigate('/author/discover')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Submit Your First Paper
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredSubmissions.map((submission) => (
                  <Card
                    key={submission._id}
                    className="hover:shadow-lg transition cursor-pointer"
                    onClick={() => navigate(`/author/submissions/${submission._id}`)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {submission.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Conference: {submission.conferenceId?.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          Track: {submission.trackId?.name || 'Unknown'}
                        </p>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Submitted: {formatDateOnlyIST(submission.createdAt)}</span>
                          {submission.feedback && (
                            <span className="text-gray-900 font-medium">Has Feedback</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(submission.status)}
                        <div className="flex gap-2">
                          {submission.status === 'revision' && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/author/submissions/${submission._id}`);
                              }}
                              className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium"
                            >
                              ✏️ Edit & Resubmit
                            </Button>
                          )}
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/author/submissions/${submission._id}`);
                            }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-sm"
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* My Papers Section (Co-Author) */}
        {statusFilter !== 'duplicate_rejected' && filteredMyPapers.length > 0 && (
          <>
            <hr className="my-8 border-gray-300" />
            
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-2xl font-bold text-gray-900">My Papers</h2>
                <Badge variant="info">Co-Author</Badge>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Papers where you are listed as co-author (view-only access)
              </p>
              
              <div className="space-y-4">
                {filteredMyPapers.map((paper) => (
                  <Card
                    key={paper._id}
                    className="hover:shadow-lg transition cursor-pointer border-l-4 border-l-blue-400"
                    onClick={() => navigate(`/author/submissions/${paper._id}`)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">
                            {paper.title}
                          </h3>
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            👥 Co-Author
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          Conference: {paper.conferenceId?.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          Track: {paper.trackId?.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-700 mb-2">
                          <span className="font-medium">Primary Author:</span> {paper.authorId?.name || 'Unknown'}
                        </p>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Submitted: {formatDateOnlyIST(paper.createdAt)}</span>
                          <span className="text-blue-600 font-medium">• View Only</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(paper.status)}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/author/submissions/${paper._id}`);
                          }}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-sm"
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
        {/* Duplicate Papers Section */}
        {(statusFilter === '' || statusFilter === 'duplicate_rejected') && duplicatePapers.length > 0 && (
          <>
            <hr className="my-8 border-gray-300" />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Duplicate Papers</h2>
                <Badge variant="danger">Rejected</Badge>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Papers that were flagged and rejected as duplicates by the conference organizer
              </p>

              <div className="space-y-4">
                {duplicatePapers.map((paper) => (
                  <Card
                    key={paper._id}
                    className="border-l-4 border-l-red-400"
                  >
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold text-gray-900">
                            {paper.title}
                          </h3>
                          {paper.isCoAuthor && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              👥 Co-Author
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          Conference: {paper.conferenceId?.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          Track: {paper.trackId?.name || 'Unknown'}
                        </p>

                        {/* Duplication Details */}
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-800 mb-1">Duplication Detection Details</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-red-700">
                            <span>
                              Status: {(paper.duplicationCheck?.status || 'unknown').replace(/_/g, ' ').toUpperCase()}
                            </span>
                            {paper.duplicationCheck?.similarityScore != null && (
                              <span>
                                Similarity: {paper.duplicationCheck.similarityScore}%
                              </span>
                            )}
                          </div>
                          {paper.rejectionDetails?.reason && (
                            <p className="text-xs text-red-600 mt-2">
                              Reason: {paper.rejectionDetails.reason}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-4 text-xs text-gray-500 mt-3">
                          <span>Submitted: {formatDateOnlyIST(paper.originalSubmittedAt || paper.createdAt)}</span>
                          <span>Archived: {formatDateOnlyIST(paper.archivedAt)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge variant="danger">Duplicate Rejected</Badge>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
